import { apiRequest, apiRoutes } from '../../api';
import { useState, useEffect, useRef, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { faIR } from 'date-fns-jalali/locale';
import 'react-datepicker/dist/react-datepicker.css';
import './MessagesPanel.css';
import OfferPanel from '../../shared/components/OfferPanel';
import appConfig from '../../config/appConfig';
import { useNavigate } from 'react-router-dom';

const WS_BASE = appConfig.api.wsURL;

const RESERVATION_STATUS_FA = {
    PEND: 'در انتظار',
    NEGOTIATING: 'در حال مذاکره',
    ACCEPTED: 'پذیرفته شده',
    COMPLETED: 'نهایی شده',
    REJECTED: 'رد شده',
    CANCELLED: 'لغو شده',
};

// ── Offer widget inside chatroom — wraps the shared OfferPanel ───────────────
function ChatOfferWidget({ room, onOpenTransaction }) {
    if (!['NEGOTIATING', 'ACCEPTED'].includes(room.reservation_status)) return null;
    if (!room.reservation_id) return null;

    const myRole = room.my_role === 'OWNER' ? 'owner' : 'buyer';

    return (
        <div className="mp-offer-widget" dir="rtl">
            <OfferPanel
                reservationId={room.reservation_id}
                myRole={myRole}
                listingType={room.listing_type || 'SALE'}
            />
            {room.transaction_id && (
                <div className="mp-transaction-handoff">
                    <strong>فرآیند معامله آغاز شده است</strong>
                    <span>پرداخت‌ها، مدارک و مراحل بعدی را در پرونده معامله دنبال کنید.</span>
                    <button type="button" onClick={() => onOpenTransaction(room.transaction_id)}>
                        ادامه فرآیند معامله
                    </button>
                </div>
            )}
        </div>
    );
}

// ── single chatroom view ─────────────────────────────────────────────────────

// ── Postpone widget ──────────────────────────────────────────────────────────
function PostponeWidget({ room, token, roomStatus, onTimeSet }) {
    const [selectedDate, setSelectedDate] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    if (roomStatus.status !== 'POSTPONED') return null;

    // The one who triggered the postpone sets the time themselves
    const canSetTime = roomStatus.can_set_postponed_time;
    const postponedTo = roomStatus.postponed_to;

    const toISO = (date) => {
        if (!date) return null;
        const pad = (n) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
    };

    const handleSubmit = async () => {
        if (!selectedDate) return;
        setError(null);
        setSubmitting(true);
        try {
            const res = await apiRequest(apiRoutes.dealbridge.roomPostponedTime(room.id), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ postponed_to: toISO(selectedDate) }),
            });
            if (!res.ok) {
                const data = await res.json();
                setError(data.detail || 'خطا');
                return;
            }
            const data = await res.json();
            onTimeSet(data);
        } catch {
            setError('خطا در ارتباط با سرور');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mp-postpone-widget" dir="rtl">
            <div className="mp-postpone-title">  جلسه موکول شد به :</div>
            {postponedTo ? (
                <div className="mp-postpone-info">
                    زمان جلسه:{' '}
                    <strong>
                        {new Date(postponedTo).toLocaleString('fa-IR', {
                            year: 'numeric', month: 'long', day: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                        })}
                    </strong>
                </div>
            ) : canSetTime ? (
                <div className="mp-postpone-form">
                    <p className="mp-postpone-desc">شما درخواست موکول شدن جلسه دادید. زمان مورد نظر خود را انتخاب کنید:</p>
                    <DatePicker
                        selected={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="yyyy/MM/dd HH:mm"
                        locale={faIR}
                        minDate={new Date(Date.now() + 60000)}
                        placeholderText="تاریخ و ساعت را انتخاب کنید"
                        className="mp-postpone-input"
                        calendarClassName="mp-jalali-calendar"
                        popperPlacement="top-end"
                    />
                    {error && <div className="mp-postpone-error">{error}</div>}
                    <button className="mp-offer-btn" onClick={handleSubmit} disabled={submitting || !selectedDate}>
                        {submitting ? '...' : 'تأیید زمان'}
                    </button>
                </div>
            ) : (
                <div className="mp-postpone-info">
                    طرف مقابل درخواست جلسه  داده است. در انتظار تعیین زمان جلسه توسط ایشان...
                </div>
            )}
        </div>
    );
}

function ChatWindow({ room, token, onBack, onOpenTransaction }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [roomStatus, setRoomStatus] = useState({ status: room.status || null });
    const wsRef = useRef(null);
    const bottomRef = useRef(null);
    const pollRef = useRef(null);
    const isNearBottomRef = useRef(true);
    const myUserId = localStorage.getItem('user_id') || '';

    // Fetch room status (postpone info) and refresh periodically when POSTPONED
    const fetchRoomStatus = useCallback(() => {
        apiRequest(apiRoutes.dealbridge.roomStatus(room.id), {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => r.ok ? r.json() : null)
            .then((data) => { if (data) setRoomStatus(data); })
            .catch(() => {});
    }, [room.id, token]);

    useEffect(() => {
        fetchRoomStatus();
    }, [fetchRoomStatus]);

    useEffect(() => {
        if (roomStatus.status !== 'POSTPONED') return;
        const t = setInterval(fetchRoomStatus, 10000);
        return () => clearInterval(t);
    }, [roomStatus.status, fetchRoomStatus]);

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    const startPolling = () => {
        if (pollRef.current) return; // already running
        pollRef.current = setInterval(() => {
            apiRequest(apiRoutes.dealbridge.chatroomMessages(room.id), {
                headers: { Authorization: `Bearer ${token}` },
            })
                .then((r) => r.json())
                .then((data) => {
                    if (Array.isArray(data)) setMessages(data);
                })
                .catch(() => { });
        }, 3000);
    };

    // Fetch history once on mount
    useEffect(() => {
        apiRequest(apiRoutes.dealbridge.chatroomMessages(room.id), {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => r.json())
            .then((data) => {
                setMessages(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [room.id, token]);

    // WebSocket with polling fallback
    useEffect(() => {
        let ws = null;
        let mounted = true;
        let fallbackTimer = null;

        const cancelFallback = () => {
            if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
        };

        const scheduleFallback = () => {
            cancelFallback();
            // Only start polling if WS hasn't connected after 4s
            fallbackTimer = setTimeout(() => {
                if (mounted && (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) {
                    startPolling();
                }
            }, 4000);
        };

        try {
            ws = new WebSocket(`${WS_BASE}/ws/chat/${room.id}/?token=${token}`);
            wsRef.current = ws;

            ws.onopen = () => {
                if (!mounted) return;
                cancelFallback();
                stopPolling(); // cancel any poll that may have started
            };

            ws.onmessage = (e) => {
                if (!mounted) return;
                const data = JSON.parse(e.data);
                if (data.type === 'chat_message') {
                    const isBot = data.message_type === 'BOT';
                    setMessages((prev) => [...prev, {
                        id: Date.now(),
                        sender_id: data.sender_id,
                        sender_username: data.sender_username,
                        sender_name: data.sender_name,
                        content: data.message,
                        message_type: data.message_type || 'USR',
                        timestamp: data.timestamp || new Date().toISOString(),
                        is_mine: !isBot && String(data.sender_id) === String(myUserId),
                    }]);
                }
                if (data.type === 'room_status') {
                    setRoomStatus((prev) => ({ ...prev, ...data }));
                }
            };

            ws.onerror = () => { if (mounted) scheduleFallback(); };
            ws.onclose = () => { if (mounted) scheduleFallback(); };
        } catch {
            if (mounted) scheduleFallback();
        }

        return () => {
            mounted = false;
            cancelFallback();
            stopPolling();
            if (ws) {
                ws.onclose = null;
                ws.onerror = null;
                ws.close();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [room.id, token, myUserId]);

    // Track whether user is near the bottom of the message list
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < 80;
    };

    // Only auto-scroll when user is already near the bottom
    useEffect(() => {
        if (isNearBottomRef.current) {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const sendMessage = () => {
        const text = input.trim();
        if (!text) return;
        setInput('');

        // Optimistic local append for the sender
        const optimistic = {
            id: `opt-${Date.now()}`,
            sender_id: myUserId,
            sender_name: 'شما',
            content: text,
            message_type: 'USR',
            timestamp: new Date().toISOString(),
            is_mine: true,
        };
        setMessages((prev) => [...prev, optimistic]);

        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ message: text }));
        } else {
            // REST fallback
            apiRequest(apiRoutes.dealbridge.chatroomMessages(room.id), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ content: text }),
            })
                .then((r) => r.json())
                .then((msg) => {
                    // Replace optimistic with real message
                    setMessages((prev) =>
                        prev.map((m) => m.id === optimistic.id ? { ...msg, is_mine: true } : m)
                    );
                })
                .catch(() => { });
        }
    };

    const handleKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    };

    return (
        <div className="mp-chat-window">
            {/* Header */}
            <div className="mp-chat-header">
                <button className="mp-back-btn" onClick={onBack}>‹ بازگشت</button>
                <div className="mp-chat-header-info">
                    <span className="mp-chat-other">{room.other_user?.full_name || room.other_user?.username}</span>
                    <span className="mp-chat-address">{room.listing_address}</span>
                </div>
                <span className="mp-chat-status">
                    {RESERVATION_STATUS_FA[room.reservation_status] || room.reservation_status}
                </span>
            </div>

            {/* Messages */}
            <div className="mp-messages-list" onScroll={handleScroll}>
                {loading && <p className="mp-loading">در حال بارگذاری...</p>}
                {messages.map((m) => {
                    const isBot = m.message_type === 'BOT';
                    const msgClass = isBot
                        ? 'mp-msg mp-msg-bot'
                        : `mp-msg ${m.is_mine ? 'mp-msg-mine' : 'mp-msg-theirs'}`;
                    return (
                        <div key={m.id} className={msgClass}>
                            {(isBot || !m.is_mine) && (
                                <span className="mp-msg-sender">{m.sender_name || m.sender_username}</span>
                            )}
                            <div className="mp-msg-bubble">{m.content}</div>
                            <span className="mp-msg-time">
                                {new Date(m.timestamp).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>

            {/* Offer widget — shown while negotiating or accepted */}
            <ChatOfferWidget room={room} onOpenTransaction={onOpenTransaction} />

            {/* Postpone widget — shown when AI detects a meeting request */}
            <PostponeWidget
                room={room}
                token={token}
                roomStatus={roomStatus}
                onTimeSet={(data) => setRoomStatus((prev) => ({ ...prev, ...data, can_set_postponed_time: false }))}
            />

            {/* Input */}
            <div className="mp-chat-input-row">
                <textarea
                    className="mp-chat-input"
                    placeholder="پیام خود را بنویسید..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKey}
                    rows={1}
                />
                <button className="mp-send-btn" onClick={sendMessage} disabled={!input.trim()}>
                    ارسال
                </button>
            </div>
        </div>
    );
}

// ── chatroom list ────────────────────────────────────────────────────────────
function ChatRoomList({ rooms, onSelect }) {
    if (rooms.length === 0) {
        return <p className="mp-empty">هیچ گفتگویی وجود ندارد.</p>;
    }

    return (
        <div className="mp-room-list">
            {rooms.map((r) => (
                <button key={r.id} className="mp-room-item" onClick={() => onSelect(r)}>
                    <div className="mp-room-top">
                        <span className="mp-room-name">
                            {r.other_user?.full_name || r.other_user?.username || 'کاربر'}
                        </span>
                        {r.unread_count > 0 && (
                            <span className="mp-unread-badge">{r.unread_count}</span>
                        )}
                    </div>
                    <div className="mp-room-address">{r.listing_address || `آگهی #${r.listing_id}`}</div>
                    {r.last_message && (
                        <div className="mp-room-preview">
                            {r.last_message.content.slice(0, 60)}
                            {r.last_message.content.length > 60 ? '...' : ''}
                        </div>
                    )}
                    <div className="mp-room-meta">
                        <span className="mp-room-status">
                            {RESERVATION_STATUS_FA[r.reservation_status] || r.reservation_status}
                        </span>
                        {r.last_message && (
                            <span className="mp-room-time">
                                {new Date(r.last_message.timestamp).toLocaleTimeString('fa-IR', {
                                    hour: '2-digit', minute: '2-digit',
                                })}
                            </span>
                        )}
                    </div>
                </button>
            ))}
        </div>
    );
}

// ── main panel ───────────────────────────────────────────────────────────────
export default function MessagesPanel({ onClose, inline = false, onUnreadChange }) {
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeRoom, setActiveRoom] = useState(null);
    const token = localStorage.getItem('access_token');
    const navigate = useNavigate();
    const openTransaction = (transactionId) => navigate(`/transactions/${transactionId}`);

    const [error, setError] = useState(null);

    const fetchRooms = useCallback(() => {
        if (!token) { setLoading(false); return; }
        apiRequest(apiRoutes.dealbridge.chatrooms, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((data) => {
                const list = Array.isArray(data) ? data : [];
                setRooms(list);
                setLoading(false);
                if (onUnreadChange) {
                    const total = list.reduce((s, r) => s + (r.unread_count || 0), 0);
                    onUnreadChange(total);
                }
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, [token, onUnreadChange]);

    useEffect(() => { fetchRooms(); }, [fetchRooms]);

    const totalUnread = rooms.reduce((s, r) => s + (r.unread_count || 0), 0);

    // When used inline inside the right sidebar, render without the fixed panel shell
    if (inline) {
        return (
            <div className="mp-inline" dir="rtl">
                {loading && <p className="mp-loading">در حال بارگذاری...</p>}
                {error && <p className="mp-loading" style={{ color: '#c62828' }}>خطا: {error}</p>}
                {!loading && !error && !activeRoom && (
                    <ChatRoomList rooms={rooms} onSelect={setActiveRoom} />
                )}
                {activeRoom && (
                    <ChatWindow
                        room={activeRoom}
                        token={token}
                        onOpenTransaction={openTransaction}
                        onBack={() => { setActiveRoom(null); fetchRooms(); }}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="mp-panel" dir="rtl">
            <div className="mp-panel-header">
                <span className="mp-panel-title">
                    💬 پیام‌ها
                    {totalUnread > 0 && <span className="mp-unread-badge">{totalUnread}</span>}
                </span>
                <button className="mp-close-btn" onClick={onClose}>✕</button>
            </div>

            {loading && <p className="mp-loading">در حال بارگذاری...</p>}
            {error && <p className="mp-loading" style={{ color: '#c62828' }}>خطا: {error}</p>}

            {!loading && !error && !activeRoom && (
                <ChatRoomList rooms={rooms} onSelect={setActiveRoom} />
            )}

            {activeRoom && (
                <ChatWindow
                    room={activeRoom}
                    token={token}
                    onOpenTransaction={openTransaction}
                    onBack={() => { setActiveRoom(null); fetchRooms(); }}
                />
            )}
        </div>
    );
}
